// Live-beskeder på bookingsiden: korte, diskrete beskeder, mens kunden booker – fx "En kunde i Valby bookede hele bilen
// for over 20 min. siden" eller "Kun 2 ledige tider tilbage i morgen".
// Reglerne (markedsføringsloven, EU's regler om vildledende knaphed og social proof, GDPR):
//  • Alt er SANDT og regnes ud fra rigtige data – en besked uden data vises aldrig. Ingen nedtællinger.
//  • Persondata: aldrig efternavn, adresse eller præcis tid. Standard er "En kunde i <by>"; kontoret kan vælge
//    "fornavn + by" (kunderne skal være informeret i privatlivspolitikken). Kunder uden markedsføringssamtykke vises aldrig.
//  • Serveren (edge-funktionen "booking", handlingen 'live') sender kun de anonymiserede beskeder her – aldrig rå data.
// Bruges af edge-funktionen, testversionen (src/app/api/demo.ts) og bookingsiden.
import { plusDage } from './medlemskab.ts';
import { dagNavn, datoTekst, iDag } from './dato.ts';
import { POSTNR_BY } from './adresser.ts';
import { rens } from './validering.ts';
import { iZone } from './geo.ts';
import type { Zone } from './typer.ts';
import { ANMELD, snitTekst } from './anmeldelse.ts';

/** Opsætning › Booking › "Live-beskeder på bookingsiden". Hver type kan slås til og fra. */
export type LiveIndst = {
  aktiv: boolean;
  booket: boolean;      // "En kunde i <by> bookede <ydelse> for <x> min. siden" (seneste 24 timer)
  faa: boolean;         // "Kun <n> ledige tider tilbage <dag>" (ud fra bookingmotoren)
  faaGraense: number;   // vises kun, når n ≤ grænsen
  omraade: boolean;     // "<n> har booket i dit område denne uge" (kun n ≥ 3)
  kigger: boolean;      // "<n> andre kigger på tider lige nu" (anonymt hjerteslag)
  bedoemmelse: boolean; // "4,8 ★ fra <n> anmeldelser" – kundernes egne anmeldelser i Freemi (anmeldelse.ts), kun n ≥ 10
  visning: 'by' | 'fornavn_by';
};
export const STANDARD_LIVE: LiveIndst = { aktiv: false, booket: true, faa: true, faaGraense: 3, omraade: true, kigger: true, bedoemmelse: true, visning: 'by' };

/** Grænser og tider. */
export const LIVE = {
  booketTimer: 24, maxBooket: 3, omraadeMin: 3, kiggerMin: 2, kiggerMinutter: 5, graenseMax: 10,
  cacheSek: 60, foersteMs: 6000, visMs: 7000, pauseMs: 15000, maxPrSession: 4,
};

export type LiveBesked =
  | { type: 'booket'; by: string; fornavn?: string; ydelse: string; siden: number }
  | { type: 'faa'; n: number; dato: string }
  | { type: 'omraade'; n: number }
  | { type: 'kigger'; n: number }
  | { type: 'bedoemmelse'; snit: number; n: number };
/** Det, bookingsiden får: kun anonymiserede beskeder og grænsen for "få tider" (null = slået fra). */
export type LiveSvar = { aktiv: boolean; faaGraense: number | null; beskeder: LiveBesked[] };
export const LIVE_SLUKKET: LiveSvar = { aktiv: false, faaGraense: null, beskeder: [] };

/** Rådata fra databasen (kun på serveren): de seneste bookinger fra kunder med samtykke, ugens antal i området og aktive besøg. */
export type LiveRaa = {
  booket: { by: string; postnr: string; fornavn?: string | null; ydelse: string; minutter: number }[];
  omraade: number | null;   // forskellige kunder, der har booket i området denne uge (null = intet område)
  kigger: number;           // aktive besøg på bookingsiden de sidste 5 min. – inkl. den, der spørger
  bedoemmelse?: { snit: number; n: number } | null;   // gennemsnit og antal af kundernes anmeldelser (brandets, hvis siden er et brands)
};

const ja = (x: unknown, std: boolean) => (typeof x === 'boolean' ? x : std);
export function normLiveIndst(x: unknown): LiveIndst {
  const v = (x && typeof x === 'object' ? x : {}) as Partial<Record<keyof LiveIndst, unknown>>;
  const s = STANDARD_LIVE;
  const g = Math.round(Number(v.faaGraense));
  return {
    aktiv: ja(v.aktiv, s.aktiv), booket: ja(v.booket, s.booket), faa: ja(v.faa, s.faa), omraade: ja(v.omraade, s.omraade), kigger: ja(v.kigger, s.kigger), bedoemmelse: ja(v.bedoemmelse, s.bedoemmelse),
    faaGraense: g >= 1 && g <= LIVE.graenseMax ? g : s.faaGraense,
    visning: v.visning === 'fornavn_by' ? 'fornavn_by' : 'by',
  };
}

/** Et anonymt besøgs-id (tilfældigt, ingen persondata). */
export const gyldigSession = (x: unknown): x is string => typeof x === 'string' && /^[a-z0-9]{16,40}$/.test(x);

/** Aldrig præcis tid: under 10 min. = 0, derefter hele 10 minutter, fra 1 time hele timer. null = for gammel/ugyldig. */
export function sidenTrin(min: number): number | null {
  if (!Number.isFinite(min) || min < 0 || min >= LIVE.booketTimer * 60) return null;
  return min < 10 ? 0 : min < 60 ? Math.floor(min / 10) * 10 : Math.floor(min / 60) * 60;
}

/** Byen/bydelen – kun bogstaver (aldrig en adresse). Ellers byen ud fra postnummeret. */
export function byNavn(by: string, postnr: string): string {
  const b = rens(by, 40);
  if (/^\p{L}[\p{L} .'-]{1,39}$/u.test(b)) return b;
  return POSTNR_BY[postnr] ?? '';
}

/** Kun fornavnet (første ord), pænt skrevet. Aldrig efternavn. */
export function fornavnAf(navn: string | null | undefined): string | undefined {
  const f = rens(navn ?? '', 120).split(' ')[0] ?? '';
  if (!/^\p{L}[\p{L}'-]{1,19}$/u.test(f)) return undefined;
  return f.charAt(0).toUpperCase() + f.slice(1).toLowerCase();
}

/** Postnumrene i kundens område: zonen, postnummeret ligger i – ellers kun postnummeret selv. */
export function omraadeIntervaller(postnr: string, zoner: Zone[]): [number, number][] | null {
  if (!/^\d{4}$/.test(postnr)) return null;
  const z = zoner.find((x) => x.intervaller.length && iZone(postnr, x));
  return z ? z.intervaller : [[Number(postnr), Number(postnr)]];
}

/** Mandag i ugen (dansk uge). */
export function ugeStart(dato: string): string {
  const w = new Date(dato + 'T12:00:00Z').getUTCDay();
  return plusDage(dato, -((w + 6) % 7));
}

/** Fra rådata til de beskeder, browseren må få – kun de typer, kontoret har slået til, og kun når de er sande. */
export function liveSvar(indst: LiveIndst, raa: LiveRaa): LiveSvar {
  if (!indst.aktiv) return LIVE_SLUKKET;
  const beskeder: LiveBesked[] = [];
  if (indst.booket) {
    const set = new Set<string>();
    for (const r of raa.booket) {
      const siden = sidenTrin(r.minutter), by = byNavn(r.by, r.postnr), ydelse = rens(r.ydelse, 60);
      if (siden == null || !by || !ydelse) continue;
      const fornavn = indst.visning === 'fornavn_by' ? fornavnAf(r.fornavn) : undefined;
      const noegle = `${fornavn ?? ''}|${by}|${ydelse}`;
      if (set.has(noegle)) continue;
      set.add(noegle);
      beskeder.push({ type: 'booket', by, ...(fornavn ? { fornavn } : {}), ydelse, siden });
      if (set.size >= LIVE.maxBooket) break;
    }
  }
  if (indst.omraade && raa.omraade != null && raa.omraade >= LIVE.omraadeMin) beskeder.push({ type: 'omraade', n: raa.omraade });
  const andre = raa.kigger - 1;
  if (indst.kigger && andre >= LIVE.kiggerMin) beskeder.push({ type: 'kigger', n: andre });
  const bd = raa.bedoemmelse;
  if (indst.bedoemmelse && bd && Number.isInteger(bd.n) && bd.n >= ANMELD.liveMin && bd.snit >= 1 && bd.snit <= 5) beskeder.push({ type: 'bedoemmelse', snit: Math.round(bd.snit * 10) / 10, n: bd.n });
  return { aktiv: true, faaGraense: indst.faa ? indst.faaGraense : null, beskeder };
}

/** Er der kun få ledige tider den dag (efter motoren)? */
export const erFaa = (n: number | undefined, graense: number | null) => graense != null && n != null && n >= 1 && n <= graense;

/** "Kun <n> ledige tider tilbage <dag>": den valgte dag, ellers den første dag med få tider. Kun ud fra motorens tal. */
export function faaBesked(dage: Record<string, number> | null | undefined, valgt: string | null, graense: number | null, idag = iDag()): LiveBesked | null {
  if (!dage || graense == null) return null;
  const dato = valgt && erFaa(dage[valgt], graense) ? valgt : Object.keys(dage).sort().find((d) => d >= idag && erFaa(dage[d], graense));
  return dato && dato >= idag ? { type: 'faa', n: dage[dato], dato } : null;
}

function dagTekst(dato: string, idag: string): string {
  if (dato === idag) return 'i dag';
  if (dato === plusDage(idag, 1)) return 'i morgen';
  if (dato <= plusDage(idag, 6)) return 'på ' + dagNavn(new Date(dato + 'T12:00:00Z').getUTCDay());
  return datoTekst(dato);
}
function sidenTekst(min: number): string {
  if (min < 10) return 'for under 10 min. siden';
  if (min < 60) return `for over ${min} min. siden`;
  const t = Math.floor(min / 60);
  return `for over ${t} ${t === 1 ? 'time' : 'timer'} siden`;
}
/** "Hele bilen" → "hele bilen" (men "SUV-pakke" bevares). */
const smaat = (s: string) => (/^\p{Lu}\p{Ll}/u.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** Beskeden som tekst på dansk. */
export function liveTekst(b: LiveBesked, idag = iDag()): string {
  switch (b.type) {
    case 'booket': return `${b.fornavn ? `${b.fornavn} fra ${b.by}` : `En kunde i ${b.by}`} bookede ${smaat(b.ydelse)} ${sidenTekst(b.siden)}`;
    case 'faa': return `Kun ${b.n} ${b.n === 1 ? 'ledig tid' : 'ledige tider'} tilbage ${dagTekst(b.dato, idag)}`;
    case 'omraade': return `${b.n} har booket i dit område denne uge`;
    case 'kigger': return `${b.n} andre kigger på tider lige nu`;
    case 'bedoemmelse': return `${snitTekst(b.snit)} ★ fra ${b.n} anmeldelser`;
  }
}

export const liveNoegle = (b: LiveBesked) => JSON.stringify(b);

/** Næste besked, der ikke er vist endnu – helst en anden type end den sidste, så det ikke bliver ensformigt. */
export function naesteBesked(kandidater: LiveBesked[], vist: ReadonlySet<string>, sidsteType: LiveBesked['type'] | null): LiveBesked | null {
  const nye = kandidater.filter((b) => !vist.has(liveNoegle(b)));
  return nye.find((b) => b.type !== sidsteType) ?? nye[0] ?? null;
}
